import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useContext, useRef } from 'react';
import { observer, useAsObservableSource } from 'mobx-react';
import { reaction } from 'mobx';
import { Route, Switch } from 'react-router-dom';

import { useStore } from 'core/store';
import CustomersResults from 'common/components/search-results/customers-results';
import OffersResults from 'common/components/search-results/offers-results';
import PoliciesResults from 'common/components/search-results/policies-results';
import BranchPlusResults from 'common/components/search-results/branch-plus-results';
import { AuthContext } from 'core/components/auth';
import { isCanonicalUser } from 'core/helpers/troubleshooting-mode';
import { useQueryParams } from 'core/hooks/useQueryParams';
import { useToast } from 'core/components/toast';
import SearchForm from './components/search-form';
import SearchHeader from './components/search-header';

const Search = observer(function Search({ location, history }) {
  const {
    quote: { setPrefillData },
    search
  } = useStore();
  const {
    loading,
    setCurrentResultsPage,
    currentResultsPage,
    setSearchString,
    setExpandedSearch,
    searchString,
    searchCustomersAction,
    searchOffersAction,
    searchPoliciesAction,
    searchBranchPlusPoliciesAction,
    customersResults,
    customersResults: { nbHits: customerNumHits },
    offersResults,
    offersResults: { nbHits: offerNumHits },
    policiesResults,
    policiesResults: { nbHits: policyNumHits },
    branchPlusPoliciesResults,
    branchPlusPoliciesResults: { nbHits: branchPlusNumHits },
    error
  } = search;
  const session = useContext(AuthContext);
  const { canEdit, canQuote, viewOnly, isInternalAgent } = session;
  // Derive the boolean here and depend on it: AuthContext's value is a fresh object each render,
  // so depending on `session` would re-run the search on every Auth re-render.
  const excludeTroubleshootingOffers = isCanonicalUser(session);
  const params = useQueryParams();
  const { notify } = useToast();
  // Tracks the phone we've already toasted for, so a Dialpad miss only notifies once per search.
  const dialpadMissNotifiedFor = useRef();

  // Set the current table page page back to 0 when the route changes and reset the search string.
  // `history.listen` returns a function that stops the listener when called,
  // which is used as the cleanup function for this effect.
  useEffect(() =>
    history.listen(() => {
      setCurrentResultsPage(0);
      setSearchString('');
    })
  );

  const runSearch = useCallback(async () => {
    // The `setOfferSearchResults` / `setUserSearchResults` functions will be called multiple times with the same
    // result when all the queued searches from the debounced search function resolve. But because they'll all resolve
    // with the same result, React's diffing will only cause one re-render, so this won't cause a performance issue.
    searchCustomersAction();
    searchOffersAction({ excludeTroubleshootingOffers });
    searchPoliciesAction(isInternalAgent);
    if (isInternalAgent) {
      searchBranchPlusPoliciesAction();
    }
  }, [
    searchCustomersAction,
    searchOffersAction,
    searchPoliciesAction,
    searchBranchPlusPoliciesAction,
    isInternalAgent,
    excludeTroubleshootingOffers
  ]);

  // The `runSearch` function changes when one of the items in its dependency array changes
  // When one of those things change we also want to run the search again.
  useEffect(() => {
    runSearch();
  }, [runSearch, currentResultsPage]);

  useEffect(() => {
    if (!canEdit || !canQuote) {
      history.replace('/search/policies');
    }
  }, [canEdit, history, canQuote]);

  // handle searching from query params, and redirect if no search results
  // need to create observablesource to work correctly with the reaction
  const source = useAsObservableSource({ inboundQuery: params?.search });

  useEffect(() => {
    if (source.inboundQuery) {
      // A Dialpad screen-pop pre-fills this search with the caller's phone number. Phone numbers are
      // only matched when Expanded Search is on (see search.service.js), so enable it first — otherwise
      // even a known member returns no results.
      if (params?.source === 'dialpad') {
        setExpandedSearch(true);
      }
      setSearchString(source.inboundQuery);
      runSearch();
    }
  }, [source.inboundQuery, setSearchString, setExpandedSearch, runSearch, params?.source]);

  // When a Dialpad screen-pop can't match the caller to a member, it pops this search pre-filled
  // with their phone number (source=dialpad). Surface an explicit toast on a no-match so the agent
  // knows nothing is broken and can search by name instead. Fires once per phone number.
  useEffect(() => {
    const isDialpadMiss =
      params?.source === 'dialpad' && !loading && customerNumHits === 0 && policyNumHits === 0 && offerNumHits === 0;
    if (isDialpadMiss && dialpadMissNotifiedFor.current !== params.search) {
      dialpadMissNotifiedFor.current = params.search;
      notify({
        type: 'info',
        message: `No member matches the incoming phone number ${params.search}. Search by name to find them.`,
        durationInSeconds: 10
      });
    }
  }, [params?.source, params?.search, loading, customerNumHits, policyNumHits, offerNumHits, notify]);

  reaction(
    () => search.offersResults,
    (results) => {
      if (results.nbHits === 0 && source.inboundQuery) {
        // search returned 0 results so redirect to new quote form with prefilled values if supplied
        if (params.firstName) {
          setSearchString(''); // or else the results dropdown will show

          // the search key/value pair from query params isn't needed in the prefill data of the new quote form
          const { search: _search, ...prefillParams } = params;
          setPrefillData({ quote: prefillParams });
          history.push('/quote');
        }
      }
    }
  );

  const onSearchStringChange = (newSearchString) => {
    setSearchString(newSearchString);
    setCurrentResultsPage(0);
    runSearch();
  };

  const onSearchButtonPress = () => {
    setCurrentResultsPage(0);
    runSearch();
  };

  return (
    <>
      <SearchHeader>
        <SearchForm
          value={searchString}
          onChange={(e) => onSearchStringChange(e.target.value)}
          onSearchButtonPress={onSearchButtonPress}
          location={location}
        />
      </SearchHeader>
      <Switch>
        {(canEdit || viewOnly) && (
          <Route path="/search/offers">
            <OffersResults
              loading={loading}
              hits={offersResults.hits}
              totalHits={offerNumHits}
              page={currentResultsPage}
              onPageChange={setCurrentResultsPage}
              showHeader
              error={error}
            />
          </Route>
        )}
        <Route path="/search/customers">
          <CustomersResults
            loading={loading}
            hits={customersResults.hits}
            totalHits={customerNumHits}
            page={currentResultsPage}
            onPageChange={setCurrentResultsPage}
            columnsToUse={['id', 'fname', 'lname', 'address', 'created']}
            useLink
            error={error}
          />
        </Route>
        <Route path="/search/policies">
          <PoliciesResults
            loading={loading}
            hits={policiesResults.hits}
            totalHits={policyNumHits}
            page={currentResultsPage}
            onPageChange={setCurrentResultsPage}
            useLink
            error={error}
          />
        </Route>
        {isInternalAgent && (
          <Route path="/search/branch-plus">
            <BranchPlusResults
              loading={loading}
              hits={branchPlusPoliciesResults.hits}
              totalHits={branchPlusNumHits}
              page={currentResultsPage}
              onPageChange={setCurrentResultsPage}
            />
          </Route>
        )}
      </Switch>
    </>
  );
});

Search.propTypes = {
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired
};

export default Search;
